import { toast } from 'react-hot-toast'

export interface ApiError {
  message: string
  code?: string
  field?: string
  details?: any
}

export interface ApiErrorResponse {
  message: string
  errors?: ApiError[]
  code?: string
  statusCode?: number
}

// Enhanced error handler for API responses
export const handleApiError = (error: any): ApiError => {
  // Network errors
  if (!error.response) {
    return {
      message: 'Network error. Please check your connection and try again.',
      code: 'NETWORK_ERROR'
    }
  }

  const { status, data } = error.response
  
  // Server errors (5xx)
  if (status >= 500) {
    return {
      message: 'Server error. Please try again later.',
      code: 'SERVER_ERROR',
      details: { status, data }
    }
  }

  // Client errors (4xx)
  if (status >= 400) {
    // Handle validation errors
    if (status === 422 && data.errors) {
      const validationErrors = data.errors.map((err: any) => ({
        message: err.message,
        field: err.field,
        code: err.code
      }))
      
      return {
        message: data.message || 'Validation failed',
        code: 'VALIDATION_ERROR',
        details: { errors: validationErrors }
      }
    }

    // Handle authentication errors
    if (status === 401) {
      return {
        message: 'Authentication required. Please sign in again.',
        code: 'AUTH_ERROR'
      }
    }

    // Handle authorization errors
    if (status === 403) {
      return {
        message: 'You do not have permission to perform this action.',
        code: 'PERMISSION_ERROR'
      }
    }

    // Handle not found errors
    if (status === 404) {
      return {
        message: data.message || 'The requested resource was not found.',
        code: 'NOT_FOUND'
      }
    }

    // Handle conflict errors
    if (status === 409) {
      return {
        message: data.message || 'A conflict occurred. The resource may already exist.',
        code: 'CONFLICT_ERROR'
      }
    }

    // Handle rate limiting
    if (status === 429) {
      return {
        message: 'Too many requests. Please wait a moment and try again.',
        code: 'RATE_LIMIT_ERROR'
      }
    }

    // Generic client error
    return {
      message: data.message || 'An error occurred while processing your request.',
      code: 'CLIENT_ERROR',
      details: { status, data }
    }
  }

  // Fallback for unknown errors
  return {
    message: 'An unexpected error occurred. Please try again.',
    code: 'UNKNOWN_ERROR',
    details: error
  }
}

// Toast notification helper for API errors
export const showApiError = (error: any, customMessage?: string): void => {
  const apiError = handleApiError(error)
  
  if (customMessage) {
    toast.error(customMessage)
  } else {
    toast.error(apiError.message)
  }

  // Log detailed error for debugging
  console.error('API Error:', {
    error: apiError,
    originalError: error
  })
}

// Retry helper for failed API calls
export const createRetryHandler = (
  maxRetries: number = 3,
  baseDelay: number = 1000
) => {
  return async <T>(
    apiCall: () => Promise<T>,
    onRetry?: (attempt: number, error: any) => void
  ): Promise<T> => {
    let lastError: any

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await apiCall()
      } catch (error) {
        lastError = error
        
        // Don't retry on client errors (4xx) except for 408, 429
        if (error && typeof error === 'object' && 'response' in error) {
          const axiosError = error as any
          if (axiosError.response?.status >= 400 && axiosError.response?.status < 500) {
            if (![408, 429].includes(axiosError.response.status)) {
              throw error
            }
          }
        }

        if (attempt === maxRetries) {
          throw error
        }

        // Calculate delay with exponential backoff
        const delay = baseDelay * Math.pow(2, attempt - 1)
        
        if (onRetry) {
          onRetry(attempt, error)
        }

        await new Promise(resolve => setTimeout(resolve, delay))
      }
    }

    throw lastError
  }
}

// Validation error helper
export const getValidationErrors = (error: any): Record<string, string> => {
  const apiError = handleApiError(error)
  
  if (apiError.code === 'VALIDATION_ERROR' && apiError.details?.errors) {
    const validationErrors: Record<string, string> = {}
    
    apiError.details.errors.forEach((err: ApiError) => {
      if (err.field) {
        validationErrors[err.field] = err.message
      }
    })
    
    return validationErrors
  }
  
  return {}
}

// Check if error is retryable
export const isRetryableError = (error: any): boolean => {
  const apiError = handleApiError(error)
  
  return [
    'NETWORK_ERROR',
    'SERVER_ERROR',
    'RATE_LIMIT_ERROR'
  ].includes(apiError.code || '')
}